// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § API contract (dates)
// ==============================================
// Calendar dates for crew paper requests. The API speaks 'YYYY-MM-DD' calendar
// dates and ISO instants; an end date means "through that day in the request's
// time zone", so a grant's expiresAt is the local midnight AFTER its last day.
// Arithmetic here is done on UTC noon-free day numbers so no browser time zone
// ever shifts a date.
// ==============================================

export const DAY_MS = 86_400_000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const parts = (date: string) => date.split('-').map(Number) as [number, number, number];
const dayNumber = (date: string) => { const [y, m, d] = parts(date); return Math.round(Date.UTC(y, m - 1, d) / DAY_MS); };
const fromDayNumber = (n: number) => new Date(n * DAY_MS).toISOString().slice(0, 10);

/** A real 'YYYY-MM-DD' calendar date (not 2026-02-30). */
export function isDate(v: string | null | undefined): v is string {
  if (!v || !DATE_RE.test(v)) return false;
  const [y, m, d] = parts(v);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

export function addDays(date: string, n: number): string { return fromDayNumber(dayNumber(date) + n); }

/** Same day n months on, clamped to the month's last day (30 Nov + 3 → 28 Feb). */
export function addMonths(date: string, n: number): string {
  const [y, m, d] = parts(date);
  const first = new Date(Date.UTC(y, m - 1 + n, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(d, last))).toISOString().slice(0, 10);
}

/** b − a in calendar days. */
export function daysBetween(a: string, b: string): number { return dayNumber(b) - dayNumber(a); }

function zoneOrUtc(tz: string | null | undefined): string {
  const t = (tz ?? '').trim();
  if (!t) return 'UTC';
  try { new Intl.DateTimeFormat('en-US', { timeZone: t }); return t; } catch { return 'UTC'; }
}

/** The local calendar date of an instant in `tz` (UTC when the zone is unknown). */
export function localDateOf(instant: string | number, tz?: string | null): string {
  const ms = typeof instant === 'number' ? instant : Date.parse(instant);
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: zoneOrUtc(tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone), year: 'numeric', month: '2-digit', day: '2-digit' });
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(new Date(ms))) p[x.type] = x.value;
  return `${p.year}-${p.month}-${p.day}`;
}

/** Today in the browser's zone (or `tz`). */
export function today(tz?: string | null, now: number = Date.now()): string { return localDateOf(now, tz); }

/** The last day of access for a grant ending at `expiresAt` (the local midnight after it). */
export function accessEndDate(expiresAt: string, tz: string | null | undefined): string {
  return localDateOf(Date.parse(expiresAt) - 1, zoneOrUtc(tz));
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'Sat 10 Oct' — the design's date, never shifted by a time zone. */
export function fmtDay(date: string | null | undefined): string {
  if (!isDate(date)) return '';
  const [y, m, d] = parts(date);
  return `${WEEKDAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MONTH[m - 1]}`;
}

/** 'Sat 17 Oct 2026' */
export function fmtDayYear(date: string | null | undefined): string {
  return isDate(date) ? `${fmtDay(date)} ${date.slice(0, 4)}` : '';
}

/** 'Sat 26 Sep · 09:12' in the browser's zone — for activity lists. */
export function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${fmtDay(localDateOf(iso))} · ${time}`;
}

/** 'Nouméa → Opua', or null when neither end is known. */
export function passageLabel(from: string | null | undefined, to: string | null | undefined): string | null {
  return [from, to].filter(Boolean).join(' → ') || null;
}

/** First name for friendly copy: 'Tony Sparks' → 'Tony'. */
export function firstName(name: string | null | undefined, fallback = 'the skipper'): string {
  return name?.trim().split(/\s+/)[0] || fallback;
}
